import React, {
    useLayoutEffect,
    useState,
} from 'react';

import {
    useNavigate,
} from 'react-router-dom';

import {
    useTranslation,
} from 'react-i18next';

import PrivacyPolicyModal
    from '../privacy/PrivacyPolicyModal';

import BrandSection
    from '../auth/components/BrandSection';

import LoginSection
    from '../auth/components/LoginSection';

import {
    useAuth,
} from '../auth/AuthContext';

import {
    ROUTES,
} from '../../config/routes';

import useHomeScrollRestoration from './hooks/useHomeScrollRestoration';

import EquipmentCard from '../equipment/components/EquipmentCard';
import { equipmentRegistry } from '../equipment/registry';
import './HomePage.css';


const PUBLIC_URL =
    process.env.PUBLIC_URL ||
    '';


const HOME_BACKGROUND_IMAGES = {
    hiCps:
        `${PUBLIC_URL}/HomeBackground/Hi-CPS.png`,

    pgSolution:
        `${PUBLIC_URL}/HomeBackground/PG-Solution.png`,

    hiLink:
        `${PUBLIC_URL}/HomeBackground/Hi-Link.png`,

    download:
        `${PUBLIC_URL}/HomeBackground/Download.png`,

    updateNotice:
        `${PUBLIC_URL}/HomeBackground/Update-Notice.png`,

    eLearning:
        `${PUBLIC_URL}/HomeBackground/E-Learning.png`,

    startNowButton:
        `${PUBLIC_URL}/HomeBackground/StartNow.png`,

    contactButton:
        `${PUBLIC_URL}/HomeBackground/Contact.png`,

    account:
        `${PUBLIC_URL}/HomeBackground/Account.png`,

    license:
        `${PUBLIC_URL}/HomeBackground/License.png`,

    successStoriesButton:
        `${PUBLIC_URL}/HomeBackground/successstories.png`,
};


export default function HomePage() {
    const {
        t,
    } =
        useTranslation(
            'app',
        );

    const {
        user,
        signIn,
    } =
        useAuth();

    const navigate =
        useNavigate();

    const [
        isPrivacyPolicyOpen,
        setIsPrivacyPolicyOpen,
    ] =
        useState(
            false,
        );


    useLayoutEffect(
        () => {
            const snapClassName =
                'home-scroll-snap';

            document.documentElement.classList.add(
                snapClassName,
            );

            document.body.classList.add(
                snapClassName,
            );

            return () => {
                document.documentElement.classList.remove(
                    snapClassName,
                );

                document.body.classList.remove(
                    snapClassName,
                );
            };
        },
        [],
    );


    useHomeScrollRestoration();


    const handleLoginSuccess =
        async () => {
            navigate(
                ROUTES.root,
                {
                    replace:
                        true,
                },
            );

            window.scrollTo({
                top:
                    0,
                behavior:
                    'smooth',
            });
        };


    const handleNavigate =
        (
            path,
        ) => {
            navigate(
                path,
            );
        };

    const userName =
        user?.userId ||
        user?.loginId ||
        'User';


    return (
        <main
            className="home-page"
        >
            <section
                className="home-hero"
            >
                <BrandSection />
                <div
                    className="home-scroll-indicator"
                >
                    <span>
                        {t(
                            'home.hero.scroll',
                        )}
                    </span>

                    <span
                        className="home-scroll-arrow"
                    >
                        ↓
                    </span>
                </div>
            </section>

            <section
                id="products"
                className="home-section home-section-light home-products-section"
            >
                <div
                    className="home-background-overlay"
                />

                <div
                    className="home-section-content home-resources-content"
                >
                    <div
                        className="home-section-number"
                    >
                        01
                    </div>

                    <div
                        className="home-section-text"
                    >
                        <h2>
                            {t(
                                'navigation.products',
                            )}
                        </h2>

                        <p>
                            {t(
                                'home.products.description',
                            )}
                        </p>

                        <div
                            className="home-products-showcase"  
                        >
                            <div
                                className="home-menu-grid home-menu-grid-products"
                            >
                                <button
                                    type="button"
                                    className="home-product-link home-product-link-hicps"
                                    onClick={() =>
                                        handleNavigate(
                                            ROUTES.hicpsIntro,
                                        )
                                    }
                                >
                                    <img
                                        src={
                                            HOME_BACKGROUND_IMAGES.hiCps
                                        }
                                        alt=""
                                    />
                                    <span>Hi-CPS</span>
                                </button>

                                <button
                                    type="button"
                                    className="home-product-link home-product-link-pg-solution"
                                    onClick={() =>
                                        handleNavigate(
                                            ROUTES.pgSolutionIntro,
                                        )
                                    }
                                >
                                    <img
                                        src={
                                            HOME_BACKGROUND_IMAGES.pgSolution
                                        }
                                        alt=""
                                    />
                                    <span>PG-Solution</span>
                                </button>

                                <button
                                    type="button"
                                    className="home-product-link home-product-link-hi-link"
                                    onClick={() =>
                                        handleNavigate(
                                            ROUTES.hiLinkIntro,
                                        )
                                    }
                                >
                                    <img
                                        src={
                                            HOME_BACKGROUND_IMAGES.hiLink
                                        }
                                        alt=""
                                    />
                                    <span>Hi-Link</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section
                id="equipment"
                className="home-section home-section-light"
            >
                <div className="home-background-overlay" />
                <div className="home-section-content home-resources-content">
                    <div className="home-section-number">02</div>
                    <div className="home-section-text">
                        <h2>{t('equipmentIntro.title')}</h2>
                        <p>{t('equipmentIntro.intro')}</p>
                        <div
                            className="home-menu-grid home-menu-grid-equipment"
                            aria-label={t('equipmentIntro.listLabel')}
                        >
                            {equipmentRegistry.map(equipment => (
                                <EquipmentCard key={equipment.id} equipment={equipment} variant="home" />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section
                id="resources"
                className="home-section home-section-light"
            >
                <div
                    className="home-background-overlay"
                />

                <div
                    className="home-section-content home-resources-content"
                >
                    <div
                        className="home-section-number"
                    >
                        03
                    </div>

                    <div
                        className="home-section-text"
                    >
                        <h2>
                            {t(
                                'navigation.resourcesGroup',
                            )}
                        </h2>

                        <p>
                            {t(
                                'home.resources.description',
                            )}
                        </p>

                        <div
                            className="home-menu-grid"
                        >
                            <button
                                type="button"
                                className="home-product-link"
                                onClick={() =>
                                    handleNavigate(
                                        ROUTES.resources,
                                    )
                                }
                            >
                                <img
                                    src={
                                        HOME_BACKGROUND_IMAGES.download
                                    }
                                    alt=""
                                />

                                <span>
                                    {t(
                                        'navigation.resources',
                                    )}
                                </span>
                            </button>

                            <button
                                type="button"
                                className="home-product-link"
                                onClick={() =>
                                    handleNavigate(
                                        ROUTES.updatenotices,
                                    )
                                }
                            >
                                <img
                                    src={
                                        HOME_BACKGROUND_IMAGES.updateNotice
                                    }
                                    alt=""
                                />

                                <span>
                                    {t(
                                        'navigation.updatenotices',
                                    )}
                                </span>
                            </button>

                            <button
                                type="button"
                                className="home-product-link"
                                onClick={() =>
                                    handleNavigate(
                                        ROUTES.successStories,
                                    )
                                }
                            >
                                <img
                                    src={
                                        HOME_BACKGROUND_IMAGES.successStoriesButton
                                    }
                                    alt=""
                                />

                                <span>
                                    {t(
                                        'navigation.successStories',
                                    )}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section
                id="customer-service"
                className="home-section home-section-light"
            >
                <div
                    className="home-background-overlay home-background-overlay-strong"
                />

                <div
                    className="home-section-content home-resources-content"
                >
                    <div
                        className="home-section-number"
                    >
                        04
                    </div>

                    <div
                        className="home-section-text"
                    >
                        <h2>
                            {t(
                                'navigation.customerService',
                            )}
                        </h2>

                        <p>
                            {t(
                                'home.customerService.description',
                            )}
                        </p>

                        <div
                            className="home-menu-grid"
                        >
                            <button
                                type="button"
                                className="home-product-link"
                                onClick={() =>
                                    handleNavigate(
                                        ROUTES.startNow,
                                    )
                                }
                            >
                                <img
                                    src={
                                        HOME_BACKGROUND_IMAGES.startNowButton
                                    }
                                    alt=""
                                />

                                <span>
                                    {t(
                                        'navigation.startNow',
                                    )}
                                </span>
                            </button>
                            
                            <button
                                type="button"
                                className="home-product-link"
                                onClick={() =>
                                    handleNavigate(
                                        ROUTES.elearning,
                                    )
                                }
                            >
                                <img
                                    src={
                                        HOME_BACKGROUND_IMAGES.eLearning
                                    }
                                    alt=""
                                />

                                <span>
                                    {t(
                                        'navigation.elearning',
                                    )}
                                </span>
                            </button>

                            <button
                                type="button"
                                className="home-product-link"
                                onClick={() =>
                                    handleNavigate(
                                        ROUTES.contact,
                                    )
                                }
                            >
                                <img
                                    src={
                                        HOME_BACKGROUND_IMAGES.contactButton
                                    }
                                    alt=""
                                />

                                <span>
                                    {t(
                                        'navigation.contact',
                                    )}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section
                id="my-info"
                className="home-section home-section-light home-my-info-section"
            >
                <div
                    className="home-background-overlay home-background-overlay-strong"
                />

                <div
                    className="home-section-content home-resources-content"
                >
                    <div
                        className="home-section-number"
                    >
                        05
                    </div>

                    <div
                        className="home-section-text"
                    >
                        <h2>
                            {t(
                                'navigation.myInfo',
                            )}
                        </h2>

                        {!user && (
                            <p>
                                {t(
                                    'home.myInfo.loginDescription',
                                )}
                            </p>
                        )}

                        {user && (
                            <p>
                                {t(
                                    'home.myInfo.welcome',
                                    {
                                        name:
                                            userName,
                                    },
                                )}
                            </p>
                        )}
                        {!user && (
                            <div
                                className="home-login-container"
                            >
                                <LoginSection
                                    loginAction={
                                        signIn
                                    }
                                    onLoginSuccess={
                                        handleLoginSuccess
                                    }
                                />
                            </div>
                        )}

                        {user && (
                            <div
                                className="home-menu-grid"
                            >
                                <button
                                    type="button"
                                    className="home-product-link"
                                    onClick={() =>
                                        handleNavigate(
                                            ROUTES.memberInfo,
                                        )
                                    }
                                >
                                    <img
                                        src={
                                            HOME_BACKGROUND_IMAGES.account
                                        }
                                        alt=""
                                    />

                                    <span>
                                        {t(
                                            'navigation.memberInfo',
                                        )}
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    className="home-product-link"
                                    onClick={() =>
                                        handleNavigate(
                                            ROUTES.licenseInfo,
                                        )
                                    }
                                >
                                    <img
                                        src={
                                            HOME_BACKGROUND_IMAGES.license
                                        }
                                        alt=""
                                    />

                                    <span>
                                        {t(
                                            'navigation.licenseInfo',
                                        )}
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <footer
                className="home-footer"
            >
                <div
                    className="home-footer-brand"
                >
                    <strong>
                        {t(
                            'home.footer.company',
                        )}
                    </strong>

                    <span>
                        {t(
                            'home.footer.portal',
                        )}
                    </span>
                </div>

                <div
                    className="home-footer-right"
                >
                    <span
                        className="home-footer-copyright"
                    >
                        {t(
                            'home.footer.copyright',
                            {
                                year:
                                    new Date().getFullYear(),
                            },
                        )}
                    </span>
                    
                    <button
                        type="button"
                        className="home-footer-privacy"
                        onClick={() =>
                            setIsPrivacyPolicyOpen(
                                true,
                            )
                        }
                    >
                        {t(
                            'home.footer.privacyPolicy',
                        )}
                    </button>
                </div>

                <PrivacyPolicyModal
                    open={
                        isPrivacyPolicyOpen
                    }
                    onClose={() =>
                        setIsPrivacyPolicyOpen(
                            false,
                        )
                    }
                />
            </footer>
        </main>
    );
}
